import { VALID_VIDEO_FORMATS, VIDEO_THUMBNAIL_MAX_WIDTH } from './constants';
import { ResourceApi } from '../../../resource/Resource';

export const checkFileWhitelist = async (files) => {
  const response = await ResourceApi.lookup('upload', 'validateFiles', {
    files,
  });
  return response;
};

// Tests are on BUFF-4308-qencode-tests branch, it was removed because of jest open handle error
// TODO: fix jest open handle error for tests and add to master
export const getFileExtension = (filename) => {
  const bits = filename.toLowerCase().split('.');

  if (bits.length > 0) {
    return bits.pop();
  }

  return null;
};

export const getFileGeneralTypeFromType = (type) => type.split('/')[0];

export const getFileGeneralType = (f) => getFileGeneralTypeFromType(f.type);

// default transcoding options
export const transcodeOptions = {
  video: {
    format: 'h264',
    width: 1280,
    height: 720,
  },
  audio: {
    format: 'mp3',
    bitrate: 196,
  },
  document: {
    thumb: {
      format: 'png',
      page: 1,
    },
  },
};

export const VIDEO_TYPES = [
  // video
  'video/h264', // non-whitelisted
  'video/divx', // non-whitelisted
  'video/avi', // non-whitelisted
  'video/quicktime',
  'video/mpeg',
  'video/mp4',
  'video/mpeg4', // non-whitelisted
  'video/ogg',
  'video/webm',
  'video/x-m4v', // non-whitelisted
  'video/x-flv',
  'video/x-matroska', // non-whitelisted
  'video/x-ms-wmv',
  'video/x-msvideo',
  'video/x-dv', // non-whitelisted
];

export const AUDIO_TYPES = [
  // audio
  'audio/aac',
  'audio/m4a',
  'audio/mpeg',
  'audio/mp3', // non-whitelisted
  'audio/mpeg3', // non-whitelisted
  'audio/ogg',
  'audio/webm',
  'audio/wav',
  'audio/mp4', // non-whitelisted
  'audio/mpegurl', // non-whitelisted
  'audio/x-m4a', // non-whitelisted
  'audio/x-mpeg-3', // non-whitelisted
  'audio/x-mpeg', // non-whitelisted
  'audio/x-wav', // non-whitelisted
  'audio/x-mpegURL', // non-whitelisted
];

export const IMAGE_TYPES = [
  'image/jpg',
  'image/jpeg',
  'image/gif',
  'image/png',
  'image/tiff',
];

export const DOCUMENT_TYPES = [
  //
  'text/plain',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.oasis.opendocument.presentation',
];

export const SPREADSHEET_TYPES = ['text/csv', 'application/vnd.ms-excel'];

// Convert a data URL to a blob object we can upload.
export const convertDataUrlToBlob = (dataURL, mimetype) => {
  const BASE64_MARKER = ';base64,';
  if (dataURL.indexOf(BASE64_MARKER) === -1) {
    const parts = dataURL.split(',');
    const raw = decodeURIComponent(parts[1]);

    return new Blob([raw], { type: mimetype });
  }

  const parts = dataURL.split(BASE64_MARKER);
  const raw = atob(parts[1]);
  const rawLength = raw.length;

  const uInt8Array = new Uint8Array(rawLength);

  for (let i = 0; i < rawLength; ++i) {
    uInt8Array[i] = raw.charCodeAt(i);
  }

  return new Blob([uInt8Array], { type: mimetype });
};

// based on https://codepen.io/aertmann/pen/mAVaPx
export const generateThumbnailFromVideo = (file) =>
  new Promise(async (resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');

    const drawThumbnail = (videoElem, callback) => {
      const canvas = document.createElement('canvas');
      const videoRatio = videoElem.videoWidth / videoElem.videoHeight;

      canvas.width = Math.min(videoElem.videoWidth, VIDEO_THUMBNAIL_MAX_WIDTH);
      canvas.height = canvas.width / videoRatio;

      try {
        canvas
          .getContext('2d')
          .drawImage(videoElem, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((previewBlob) => {
          const outputBlob = previewBlob;
          outputBlob.name = `${file.name.replace(/(\..+)?$/, '')}_preview.jpg`;

          if (callback) {
            callback();
          }

          resolve(outputBlob);
        }, 'image/jpeg');
      } catch (e) {
        reject(Error(e.name));
      }
    };

    const timeupdate = () => {
      drawThumbnail(video, () => {
        video.pause();
        video.removeEventListener('timeupdate', timeupdate);
        URL.revokeObjectURL(url);
      });
    };

    video.addEventListener('loadeddata', timeupdate);
    video.addEventListener('timeupdate', timeupdate);

    // catch error when the video is not supported by browser
    try {
      video.preload = 'metadata';
      video.src = url;
      // Load video in Safari / IE11
      video.muted = true;
      video.playsInline = true;

      await video.play();
    } catch (e) {
      URL.revokeObjectURL(url);
      reject(Error(e.name));
    }
  });

export const checkVideoFormat = (file) =>
  new Promise((resolve) => {
    import('mp4filechecker').then((mp4filechecker) => {
      mp4filechecker.checkMp4File(file, VALID_VIDEO_FORMATS).then(resolve);
    });
  });

export const isInvalidFileExtentionError = (error) => {
  const regex = /Extension not permitted/;
  const errorMessage = error.message || error;

  return regex.test(errorMessage);
};
